import { lazy, Suspense, useEffect, useState } from 'react';
import * as React from 'react';

import Image from '@/components/elements/Image';
import { useUserContext } from '@/contexts/user';
import useModal from '@/hooks/modal/useModal';
import useHashScroll from '@/hooks/scroll/useHashScroll/HashScroll';
import {
  useRegisterEntryAddTypeMutation,
  useRegisterEntryCampaignMutation,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { Campaign } from '@/lib/microcms/model';
import { htmlToThemed } from '@/lib/microcms/parseHTML';
import { parseCmsSrcset } from '@/lib/microcms/utils';
import { ENTERING_KEY } from '@/pages/News/Id';
import { defaultTheme } from '@/utility/theme';

import ShareLinks from '../Partials/ShareLinks';
import { BodyWrapper, ContentHeader, PublishedAt, Title } from '../style';
import FloatEntryButton from './FloatEntryButton';

const LoginModal = lazy(() => import('@/components/layouts/Modal/Login'));
const ErrorModal = lazy(() => import('@/components/layouts/Modal/Error'));
const CpConfirmModal = lazy(
  () => import('@/components/layouts/Modal/CpConfirm'),
);

interface CampaignContentBodyProps {
  content: Campaign;
  isEnteredEntryCampaign: boolean;
  selectCampaignTitle: string[];
  entryCampaignId: string;
}

const CampaignContentBody: React.FC<CampaignContentBodyProps> = ({
  content,
  isEnteredEntryCampaign,
  selectCampaignTitle,
  entryCampaignId,
}) => {
  const { isMember } = useUserContext();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);
  const [entryButton, setEntryButton] = useState<HTMLButtonElement | null>();
  const [entryButtonGroup, setEntryButtonGroup] =
    useState<NodeListOf<HTMLElement> | null>();
  const [isOpenLoginModal, setIsOpenLoginModal] = useModal(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useModal(false);
  const [isEntered, setIsEntered] = useState<boolean>(isEnteredEntryCampaign);

  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');
  const [confirmModalType, setConfirmModalType] = useState<string>('');

  const [registerEntryCampaignMutation, registerEntryCampaignMutationResult] =
    useRegisterEntryCampaignMutation();
  const [registerEntryAddTypeMutation, registerEntryAddTypeMutationResult] =
    useRegisterEntryAddTypeMutation();

  const toDisabled = () => {
    entryButton!.style!.cursor = 'inherit';
    entryButton!.style!.opacity =
      defaultTheme.button.opacity.disabled.toString();
    entryButton!.setAttribute('disabled', 'true');
    entryButton!.innerHTML = 'エントリー済み';
  };

  const toSelectedEntry = (ecid: string) => {
    const btnGroupInfo = document.getElementById('btn-group-info');
    const btnGroup = document.getElementById('btn-group');
    const entid = content.id;
    let titleIdx = -1;
    btnGroup!.style.height = '0px';
    entryButtonGroup!.forEach((eb, idx) => {
      eb.hidden = true;
      if (ecid && `${entid}_${eb.id}` == ecid) {
        titleIdx = idx;
      }
    });
    btnGroupInfo!.hidden = false;
    if (ecid) {
      btnGroupInfo!.innerHTML = `「${selectCampaignTitle[titleIdx]}」にエントリー済みです`;
    } else {
      btnGroupInfo!.innerHTML = `現在エントリー期間外です`;
    }
  };

  const entryCampaignFnc = () => {
    if (isProcessing) return;
    if (!isMember) {
      setIsOpenLoginModal(true);
      return;
    }
    setIsProcessing(true);
    registerEntryCampaignMutation({
      variables: {
        entryCampaignId: content.id,
      },
    })
      .then(_ => {
        toDisabled();
        setIsEntered(true);
      })
      .catch(() => {
        setIsOpenErrorModal(true);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  const entryCpGroupFnc = (idx: number, type: string) => {
    if (isProcessing) return;
    if (!isMember) {
      setIsOpenLoginModal(true);
      return;
    }

    setConfirmModalTitle(`「${selectCampaignTitle[idx]}」にエントリーします`);
    setConfirmModalType(type);
    setIsOpenConfirmModal(true);
  };

  const entryCpGroup = (type: string) => {
    setIsOpenConfirmModal(false);
    setIsProcessing(true);
    console.log(`${content.id}_${type} にエントリー`);
    // return;

    registerEntryAddTypeMutation({
      variables: {
        entryCampaignId: content.id,
        entryType: type,
      },
    })
      .then(res => {
        if (res.data) {
          toSelectedEntry(res.data.registerEntryAddType.entryCampaignId);
          setIsEntered(true);
        }
      })
      .catch(() => {
        setIsOpenErrorModal(true);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  useEffect(() => {
    setEntryButton(
      document.getElementById('entry-button') as HTMLButtonElement | null,
    );

    const names = document.getElementsByName('entry-button-group');
    setEntryButtonGroup(names);
  }, []);

  useEffect(() => {
    setIsEntered(isEnteredEntryCampaign);

    if (entryButton && content) {
      entryButton.addEventListener('click', entryCampaignFnc);
      if (isEnteredEntryCampaign) {
        toDisabled();
      } else if (!dayjst().isBetween(content.startTime, content.endTime)) {
        toDisabled();
        entryButton.innerHTML = '現在エントリー期間外です';
      }
    }
    return () => {
      if (entryButton) {
        entryButton.removeEventListener('click', entryCampaignFnc);
      }
    };
  }, [isEnteredEntryCampaign, entryButton]);

  useEffect(() => {
    const btnGroupInfo = document.getElementById('btn-group-info');
    const btnGroup = document.getElementById('btn-group');
    setIsEntered(isEnteredEntryCampaign);

    if (entryButtonGroup && btnGroupInfo && btnGroup && content) {
      if (isEnteredEntryCampaign) {
        toSelectedEntry(entryCampaignId);
      } else if (!dayjst().isBetween(content.startTime, content.endTime)) {
        toSelectedEntry('');
      } else {
        btnGroupInfo.hidden = true;
        entryButtonGroup.forEach((eb, idx) => {
          const btnid = eb.id;
          eb.innerText = `${selectCampaignTitle[idx]}にエントリーする`;
          eb.addEventListener('click', () => {
            entryCpGroupFnc(idx, btnid);
          });
          eb.hidden = false;
        });
      }
    }
  }, [
    isEnteredEntryCampaign,
    selectCampaignTitle,
    entryCampaignId,
    entryButtonGroup,
  ]);

  useHashScroll();

  return (
    <>
      <Image
        srcSet={parseCmsSrcset({ imageUrl: content.featuredImage.url })}
        mediaSet={[
          {
            srcSet: parseCmsSrcset({
              imageUrl: content.featuredImage.url,
              format: 'webp',
            }),
            type: 'image/webp',
          },
        ]}
        aspectRatio={content.featuredImage.height / content.featuredImage.width}
      />
      <ContentHeader>
        <PublishedAt>
          {dayjst(content.publishedAt || content.updatedAt).format('YYYY/M/D')}
        </PublishedAt>
        <Title>{content.title}</Title>
      </ContentHeader>
      <BodyWrapper>
        {htmlToThemed(content.body_v2 || content.body || '')}
      </BodyWrapper>
      <ShareLinks nonPublic={content.nonPublic} />
      <Suspense fallback={<></>}>
        <LoginModal
          isOpen={isOpenLoginModal}
          handleClose={() => setIsOpenLoginModal(false)}
          backPath={`/campaign/${content.id}`}
          urlParams={
            new URLSearchParams(
              selectCampaignTitle.length == 0 ? `` : `${ENTERING_KEY}=true`,
            )
          }
        />
      </Suspense>
      <Suspense fallback={<></>}>
        <CpConfirmModal
          isOpen={isOpenConfirmModal}
          handleClose={() => setIsOpenConfirmModal(false)}
          title={confirmModalTitle}
          type={confirmModalType}
          action={entryCpGroup}
        />
      </Suspense>
      <Suspense fallback={<></>}>
        <ErrorModal
          isOpen={isOpenErrorModal}
          handleClose={() => setIsOpenErrorModal(false)}
          apolloError={
            registerEntryCampaignMutationResult.error ||
            registerEntryAddTypeMutationResult.error
          }
        />
      </Suspense>
      {content && selectCampaignTitle.length == 0 && (
        <FloatEntryButton
          isEntered={isEntered}
          campaign={content}
          onClick={() => entryCampaignFnc()}
        />
      )}
    </>
  );
};

export default CampaignContentBody;
